// Typed client wrappers for /api/ops/intake (OI-2/OI-3).
//
// Auth + workspace-org headers reuse the same `opsFetch` primitive the rest
// of the ops/* surface uses. The endpoint dispatches by HTTP method:
//   GET  ?op=list&status=...&source=... -> { rows, total }
//   POST ?op=create | accept | hold | cancel  -> { intake, ... }
import { opsFetch, type FetchResult } from "@/lib/opsPaperworkClientBase";
import type { IdeaRoomIntakeCorrelation } from "@/lib/ideaRoomCorrelationTypes";

export * from "@/lib/orderIntakeCustomersClient";

const BASE = "/api/ops/intake";

export type OpsOrderIntakeStatus =
  | "pending_review"
  | "on_hold"
  | "accepted"
  | "cancelled";

export type OpsOrderIntakeSource =
  | "inventory_host_customer_order"
  | "inventory_host_inventory_order"
  | "in_app_customer"
  | "in_app_inventory"
  | "in_app_dealer"
  | "manual_import"
  | "idearoom_export"
  | "sensei_export";

export type OpsOrderIntakeKind = "customer" | "inventory";

export type OpsOrderIntakeSalePath =
  | "inventory_no_sale_yet"
  | "cash"
  | "finance"
  | "rto";

export type OpsOrderIntakeRow = {
  id: string;
  organization_id: string;
  source: OpsOrderIntakeSource;
  external_source_id: string | null;
  kind: OpsOrderIntakeKind;
  status: OpsOrderIntakeStatus;
  hold_reason: string | null;
  cancelled_at: string | null;
  decided_by_user_id: string | null;
  decided_at: string | null;
  submitted_by_user_id: string | null;
  submitted_by_org_id: string | null;
  proposed_plant_id: string | null;
  proposed_seller_location_id: string | null;
  proposed_model_id: string | null;
  proposed_options: Record<string, unknown>;
  proposed_sale_path: OpsOrderIntakeSalePath | null;
  proposed_inventory_holder_org_id: string | null;
  proposed_end_customer_contact_id: string | null;
  raw_payload: Record<string, unknown>;
  payload_history: unknown[];
  materialized_deal_id: string | null;
  materialized_unit_id: string | null;
  idearoom_correlation?: IdeaRoomIntakeCorrelation | null;
  created_at: string;
  updated_at: string;
};

export type ListIntakesResponse = {
  ok: true;
  rows: OpsOrderIntakeRow[];
  total: number | null;
};

export type ListIntakesParams = {
  status?: OpsOrderIntakeStatus | OpsOrderIntakeStatus[];
  source?: OpsOrderIntakeSource | OpsOrderIntakeSource[];
  kind?: OpsOrderIntakeKind;
  limit?: number;
  offset?: number;
};

function buildListQuery(params: ListIntakesParams): string {
  const sp = new URLSearchParams({ op: "list" });
  if (params.status) {
    sp.set(
      "status",
      Array.isArray(params.status) ? params.status.join(",") : params.status
    );
  }
  if (params.source) {
    sp.set(
      "source",
      Array.isArray(params.source) ? params.source.join(",") : params.source
    );
  }
  if (params.kind) sp.set("kind", params.kind);
  if (typeof params.limit === "number") sp.set("limit", String(params.limit));
  if (typeof params.offset === "number") sp.set("offset", String(params.offset));
  return `${BASE}?${sp.toString()}`;
}

export function listOrderIntakes(
  params: ListIntakesParams = {}
): Promise<FetchResult<ListIntakesResponse>> {
  return opsFetch<ListIntakesResponse>(buildListQuery(params), { method: "GET" });
}

export type ListDealerIntakesParams = {
  status?: OpsOrderIntakeStatus | OpsOrderIntakeStatus[];
  manufacturer_org_id?: string;
  limit?: number;
  offset?: number;
};

/** Rows this org submitted (dealer-create, own-org create, etc.). */
export function listDealerOrderIntakes(
  params: ListDealerIntakesParams = {},
): Promise<FetchResult<ListIntakesResponse>> {
  const sp = new URLSearchParams({ op: "dealer-list" });
  if (params.status) {
    sp.set(
      "status",
      Array.isArray(params.status) ? params.status.join(",") : params.status,
    );
  }
  if (params.manufacturer_org_id) sp.set("manufacturer_org_id", params.manufacturer_org_id);
  if (typeof params.limit === "number") sp.set("limit", String(params.limit));
  if (typeof params.offset === "number") sp.set("offset", String(params.offset));
  return opsFetch<ListIntakesResponse>(`${BASE}?${sp.toString()}`, { method: "GET" });
}

export type CreateIntakeBody = {
  source: OpsOrderIntakeSource;
  kind: OpsOrderIntakeKind;
  proposed_plant_id?: string | null;
  proposed_seller_location_id?: string | null;
  proposed_model_id?: string | null;
  proposed_options?: Record<string, unknown>;
  proposed_sale_path?: OpsOrderIntakeSalePath | null;
  proposed_inventory_holder_org_id?: string | null;
  proposed_end_customer_contact_id?: string | null;
  raw_payload?: Record<string, unknown>;
};

export type CreateIntakeResponse = {
  ok: true;
  intake: OpsOrderIntakeRow & { merged?: boolean };
};

export function createOrderIntake(
  body: CreateIntakeBody
): Promise<FetchResult<CreateIntakeResponse>> {
  return opsFetch<CreateIntakeResponse>(`${BASE}?op=create`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function importIdeaRoomOrderIntake(payload: unknown): Promise<FetchResult<CreateIntakeResponse>> {
  return opsFetch<CreateIntakeResponse>(`${BASE}?op=idearoom-import`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ payload }),
  });
}

export function importSenseiOrderIntake(payload: unknown): Promise<FetchResult<CreateIntakeResponse>> {
  return opsFetch<CreateIntakeResponse>(`${BASE}?op=sensei-import`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ payload }),
  });
}

export type DealerCreateIntakeBody = {
  target_manufacturer_org_id: string;
  proposed_seller_location_id: string;
  proposed_sale_path: OpsOrderIntakeSalePath;
  proposed_end_customer_contact_id: string;
  proposed_model_id?: string | null;
  proposed_options?: Record<string, unknown>;
  raw_payload?: Record<string, unknown>;
  /** OI-5b: Hold-resubmit-merge into this on_hold row (same intake id). */
  resubmit_intake_id?: string | null;
};

export type DealerCreateIntakeResponse = {
  ok: true;
  intake: OpsOrderIntakeRow & { merged?: boolean };
  plant_source?: string;
};

export type LegacyPosHandoffIntakeBody = DealerCreateIntakeBody;

export type LegacyPosHandoffIntakeResponse = {
  ok: true;
  handoff_id: string;
  legacy_pos_entry_url: string;
  target_manufacturer_org_id: string;
  legacy_pos_contact_id: string | null;
};

/** Legacy manufacturer path — records handoff audit row; order completes in the POS. */
export function legacyPosHandoffOrderIntake(
  body: LegacyPosHandoffIntakeBody,
): Promise<FetchResult<LegacyPosHandoffIntakeResponse>> {
  return opsFetch<LegacyPosHandoffIntakeResponse>(`${BASE}?op=legacy-pos-handoff`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/** OI-5a: dealer org submits an in_app_dealer intake to a target manufacturer. */
export function dealerCreateOrderIntake(
  body: DealerCreateIntakeBody,
): Promise<FetchResult<DealerCreateIntakeResponse>> {
  return opsFetch<DealerCreateIntakeResponse>(`${BASE}?op=dealer-create`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export type AcceptIntakeResponse = {
  ok: true;
  intake: OpsOrderIntakeRow;
  deal: { id: string };
  unit: { id: string };
};

export function acceptOrderIntake(payload: {
  intake_id: string;
  plant_id_override?: string | null;
}): Promise<FetchResult<AcceptIntakeResponse>> {
  return opsFetch<AcceptIntakeResponse>(`${BASE}?op=accept`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export type HoldIntakeResponse = { ok: true; intake: OpsOrderIntakeRow };

export function holdOrderIntake(payload: {
  intake_id: string;
  hold_reason: string;
}): Promise<FetchResult<HoldIntakeResponse>> {
  return opsFetch<HoldIntakeResponse>(`${BASE}?op=hold`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export type CancelIntakeResponse = { ok: true; intake: OpsOrderIntakeRow };

export function cancelOrderIntake(payload: {
  intake_id: string;
}): Promise<FetchResult<CancelIntakeResponse>> {
  return opsFetch<CancelIntakeResponse>(`${BASE}?op=cancel`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export type ReturnToHoldResponse = { ok: true; intake: OpsOrderIntakeRow };

/**
 * OI-6a: revert an accepted intake back to on_hold. The materialized deal
 * is archived (status -> 'archived') but materialized_deal_id /
 * materialized_unit_id stay populated so a subsequent resubmit + re-Accept
 * reactivates the same deal/unit (preserves serial + contract drafts).
 */
export function returnOrderIntakeToHold(payload: {
  intake_id: string;
  hold_reason: string;
}): Promise<FetchResult<ReturnToHoldResponse>> {
  return opsFetch<ReturnToHoldResponse>(`${BASE}?op=return-to-hold`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export * from "./orderIntakeEventsClient";
